// Headline/subtitle resolver for SignInModal. The modal renders on a dozen
// surfaces (packet overlay, app header, sidebar, folder, promo, subscription,
// join-org, ...) and its copy now varies by surface as well as by experiment, so
// the precedence lives here instead of as nested ternaries inside the JSX.
//
// Precedence, highest first:
//   1. An explicit `headline` / `subtitle` prop, or `premiumSignIn` — the call
//      site knows something about its context that an experiment shouldn't
//      override (a school-premium pitch outranks a copy A/B).
//   2. The referrer's name, when the opener resolved one. `referrerGivenName`
//      arrives already filtered by routes/Base/ReferralWelcomePrompt — the
//      homepage surface, the experiment-referral-signup-name arm and identify
//      having resolved are all decided there, next to the lookup that produces
//      the name, so this resolver stays a pure "name in, line out".
//   3. Surface-scoped experiment copy, keyed on `source`.
//   4. The pre-existing per-surface defaults.
//
// Experiment copy is inert until `isLDidentified`, the same rule as
// resolvePlanTier and for the same reason: before identify resolves,
// withLDProvider evaluates against an anonymous context that has no mpDeviceId,
// and a context missing the bucketBy attribute hashes to bucket 0.0 — so every
// such visitor is served the FIRST variation rather than a random one (see the
// comment on the identify effect in routes/Base/base.jsx). Rendering that value
// would show the wrong arm to exactly the logged-out population these copy
// experiments target, and then swap the copy under the user mid-view when
// identify lands.
export const SIGN_IN_SOURCE = {
  PACKET: 'packet',
};

// Source for the global sign-in surfaces — the signed-out sidebar and the
// forced-signup modal — which mount on every route, so "which component opened
// it" doesn't say whether a resource is on screen. They pass their current
// pathname instead, and a visitor looking at a packet or project gets the same
// copy however they start signing in.
//
// Deliberately the same substring test as the existing isPacketOrProject checks
// in AppSidebar and AppHeader, so all three agree on what counts as a resource
// route (the deprecated /packet/:id paths redirect to /p/:id before any modal
// can open, so they need no case of their own).
export const signInSourceForPath = (pathname = '') =>
  pathname.includes('/p/') || pathname.includes('/project/') ? SIGN_IN_SOURCE.PACKET : '';

// One wording for the referral line wherever it shows: the entry screen's headline and the
// sign-up step's subtext, so the visitor sees the same sentence either side of picking email.
// Empty when nothing resolved, which is what both call sites test on.
export const referrerInviteLine = (referrerGivenName = '') =>
  referrerGivenName ? `${referrerGivenName} invited you to Diffit!` : '';

// Scoping experiment copy by `source` is what keeps packet-specific wording off
// the header and sidebar sign-in buttons, which mount on every route. The
// trade-off is dilution, not bias: a bucketed-treatment user who signs in from
// the header instead of the packet overlay sees control copy, but so does their
// counterpart in the control arm, so the arms stay comparable and only the
// effect size shrinks.
export const resolveSignInCopy = ({
  headline = '',
  subtitle = '',
  source = '',
  isLDidentified = false,
  premiumSignIn = false,
  experimentPacketSignInCopy = false,
  referrerGivenName = '',
}) => {
  // experiment-packet-sign-in-copy: on = packet-specific framing of the value of
  // an account, off = the generic default copy.
  const packetCopyExperiment =
    isLDidentified && !!experimentPacketSignInCopy && source === SIGN_IN_SOURCE.PACKET;

  const resolveHeadline = () => {
    if (headline) return headline;
    if (premiumSignIn) return "Join your school's premium subscription!";
    if (referrerGivenName) return referrerInviteLine(referrerGivenName);
    if (packetCopyExperiment) return 'Welcome to Diffit!';
    return "Welcome! Let's get started.";
  };

  const resolveSubtitle = () => {
    if (subtitle) return subtitle;
    // The packet-copy arm is scoped to one surface and outranks the default: on
    // the packet overlay its subtitle shadows the standard one.
    if (packetCopyExperiment) return 'Create a free account to edit this resource';
    return 'Sign up or log in to continue';
  };

  return { headline: resolveHeadline(), subtitle: resolveSubtitle() };
};
